import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PageTransition } from "@/components/motion";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { buildAuthUrl, getPendingAuth, sanitizeReturnTo } from "@/lib/authFlow";
import { StoreProvider } from "@/store";
import { useTheme } from "@/hooks/useTheme";
import { useSubscriptionAnimation } from "@/hooks/useSubscriptionAnimation";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Skeleton } from "@/components/ui/skeleton";
import { SubscriptionTransition } from "@/components/SubscriptionTransition";
import { ArmadilloProvider } from "@/security/ArmadilloProvider";
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import Detail from "./pages/Detail.tsx";
import EditBounty from "./pages/EditBounty.tsx";
import NotFound from "./pages/NotFound.tsx";

// Lazy loaded routes for better performance
const Post = lazy(() => import("./pages/Post.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const PublicProfile = lazy(() => import("./pages/PublicProfile.tsx"));
const Analytics = lazy(() => import("./pages/Analytics.tsx"));
const Subscription = lazy(() => import("./pages/Subscription.tsx"));
const SettingsPage = lazy(() => import("./pages/Settings.tsx"));
const DepositPage = lazy(() => import("./pages/Deposit.tsx"));
const Onboarding = lazy(() => import("./pages/Onboarding.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const OpsCommand = lazy(() => import("./pages/OpsCommand.tsx"));
const Wall = lazy(() => import("./pages/Wall.tsx"));
const WallEntry = lazy(() => import("./pages/WallEntry.tsx"));
const TaskCategory = lazy(() => import("./pages/TaskCategory.tsx"));
const SellWebsite = lazy(() => import("./pages/SellWebsite.tsx"));
const Marketplace = lazy(() => import("./pages/Marketplace.tsx"));
const ListingDetail = lazy(() => import("./pages/ListingDetail.tsx"));
const FoundersWall = lazy(() => import("./pages/FoundersWallPage.tsx"));

const queryClient = new QueryClient();

// Loading fallback component for lazy routes
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4">
    <div className="relative h-12 w-12">
      <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
    </div>
    <Skeleton className="h-4 w-32" />
  </div>
);

// Theme Controller Component
const ThemeController = () => {
  useTheme();
  return null;
};

// Subscription Animation Controller Component
const SubscriptionAnimationController = () => {
  const { animationState, completeAnimation } = useSubscriptionAnimation();
  
  if (animationState === "none") return null;
  
  // Map animation states to component types
  const typeMap: Record<string, "upgrade" | "downgrade" | "expired"> = {
    upgrading: "upgrade",
    downgrading: "downgrade",
    expired: "expired",
  };
  
  return (
    <SubscriptionTransition
      type={typeMap[animationState]}
      onComplete={completeAnimation}
    />
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { isPasswordRecovery } = useAuth();

  if (isPasswordRecovery && location.pathname !== "/auth") {
    const returnTo = sanitizeReturnTo(getPendingAuth()?.returnTo ?? location.pathname);
    return <Navigate to={`${buildAuthUrl(returnTo, "signin")}&reset=true`} replace />;
  }

  return (
    <PageTransition>
      <Routes location={location}>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Onboarding />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/post"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Post />
                  </Suspense>
                }
              />
              <Route path="/bounty/:id" element={<Detail />} />
              <Route path="/edit/:id" element={<EditBounty />} />
              <Route path="/profile/:userId" element={<Suspense fallback={<PageLoader />}><PublicProfile /></Suspense>} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Profile />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Analytics />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route path="/subscription" element={<Suspense fallback={<PageLoader />}><Subscription /></Suspense>} />
              <Route
                path="/deposit"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <DepositPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <SettingsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Admin />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ops-command"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <OpsCommand />
                  </Suspense>
                }
              />
              <Route path="/wall" element={<Suspense fallback={<PageLoader />}><Wall /></Suspense>} />
              <Route path="/wall/:slug" element={<Suspense fallback={<PageLoader />}><WallEntry /></Suspense>} />
              <Route path="/tasks/:slug" element={<Suspense fallback={<PageLoader />}><TaskCategory /></Suspense>} />
              <Route path="/sell" element={<Suspense fallback={<PageLoader />}><SellWebsite /></Suspense>} />
              <Route path="/marketplace" element={<Suspense fallback={<PageLoader />}><Marketplace /></Suspense>} />
              <Route path="/listing/:id" element={<Suspense fallback={<PageLoader />}><ListingDetail /></Suspense>} />
              <Route path="/redeem" element={<Navigate to="/wall" replace />} />
              <Route path="/founders-wall" element={<Suspense fallback={<PageLoader />}><FoundersWall /></Suspense>} />
              <Route path="*" element={<NotFound />} />
      </Routes>
    </PageTransition>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ErrorBoundary>
          <AuthProvider>
            <StoreProvider>
              <ThemeController />
              <SubscriptionAnimationController />
              <ArmadilloProvider>
                <AppRoutes />
              </ArmadilloProvider>
            </StoreProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
